import { state, getActiveSequence, pushUndo, notify, keyframeToText } from './state.js';
import { showToast, showConfirmModal, showPromptModal } from './ui.js';
import { importSequenceFromJSON, exportSequenceJSON, exportHTML, exportAllHTML, exportFullJSON, copyJS } from './export.js';
import { renderAll } from './render-bridge.js';
import { isAuthenticated } from '../lib/auth.js';
import { isSupabaseConfigured } from '../lib/supabase.js';
import { saveSequenceToCloud, publishSequence, unpublishSequence, deleteSequenceFromCloud } from '../lib/storage.js';

// ================================================================
// TIMELINE
// ================================================================
let dragState = null;

// ================================================================
// SEQUENCE MANAGER
// ================================================================
export function renderSequenceManager() {
  const container = document.getElementById("timeline-seq-manager");
  if (!container) return;
  container.innerHTML = "";

  const activeIdx = state.ui.activeSequenceIdx || 0;

  // Row 1: tabs
  const tabsDiv = document.createElement("div");
  tabsDiv.className = "seq-tabs";

  state.project.sequences.forEach((seq, idx) => {
    const tab = document.createElement("button");
    tab.className = "seq-tab" + (idx === activeIdx ? " active" : "");
    tab.textContent = seq.name || ("Timeline " + (idx + 1));
    tab.addEventListener("click", () => {
      stopPlayback();
      state.ui.activeSequenceIdx = idx;
      state.ui.playbackStep = 0;
      renderSequenceManager();
      renderTimeline();
      renderTimelinePreview();
      renderTimelineControls();
    });
    tabsDiv.appendChild(tab);
  });

  const newBtn = document.createElement("button");
  newBtn.className = "seq-tab seq-new";
  newBtn.textContent = "+ New Timeline";
  newBtn.addEventListener("click", addNewSequence);
  tabsDiv.appendChild(newBtn);

  container.appendChild(tabsDiv);

  // Row 2: actions for active sequence
  const actionsDiv = document.createElement("div");
  actionsDiv.className = "seq-actions";

  const activeSeq = state.project.sequences[activeIdx];

  // Cloud status badge for the active sequence
  if (isSupabaseConfigured()) {
    const badge = document.createElement("span");
    badge.className = "seq-badge";
    if (!isAuthenticated()) {
      badge.classList.add("seq-badge-offline");
      badge.textContent = "Local (sign in to sync)";
    } else if (activeSeq?._isPublic) {
      badge.classList.add("seq-badge-public");
      badge.textContent = "\u{1F310} Public";
    } else if (activeSeq?._cloudId) {
      badge.classList.add("seq-badge-saved");
      badge.textContent = "\u25CF Saved to cloud";
    } else {
      badge.classList.add("seq-badge-local");
      badge.textContent = "Local only";
    }
    actionsDiv.appendChild(badge);
  }

  const renameBtn = document.createElement("button");
  renameBtn.className = "btn-sm";
  renameBtn.textContent = "Rename";
  renameBtn.addEventListener("click", renameActiveSequence);
  actionsDiv.appendChild(renameBtn);

  const dupBtn = document.createElement("button");
  dupBtn.className = "btn-sm";
  dupBtn.textContent = "Duplicate";
  dupBtn.addEventListener("click", duplicateActiveSequence);
  actionsDiv.appendChild(dupBtn);

  const sep = document.createElement("span");
  sep.style.cssText = "width:1px;background:var(--border);height:16px;margin:0 2px;";
  actionsDiv.appendChild(sep);

  const importBtn = document.createElement("button");
  importBtn.className = "btn-sm";
  importBtn.textContent = "Import JSON";
  importBtn.addEventListener("click", importSequenceFromJSON);
  actionsDiv.appendChild(importBtn);

  const exportBtn = document.createElement("button");
  exportBtn.className = "btn-sm";
  exportBtn.textContent = "Export JSON";
  exportBtn.addEventListener("click", exportSequenceJSON);
  actionsDiv.appendChild(exportBtn);

  // Cloud actions for this specific sequence
  if (isSupabaseConfigured()) {
    const sepCloud = document.createElement("span");
    sepCloud.style.cssText = "width:1px;background:var(--border);height:16px;margin:0 2px;";
    actionsDiv.appendChild(sepCloud);

    const saveBtn = document.createElement("button");
    saveBtn.className = "btn-sm btn-cloud";
    saveBtn.textContent = activeSeq?._cloudId ? "Update" : "Save to Cloud";
    saveBtn.disabled = !isAuthenticated();
    if (saveBtn.disabled) saveBtn.title = "Sign in to save to cloud";
    saveBtn.addEventListener("click", async () => {
      if (!isAuthenticated()) {
        document.querySelector('.auth-signin-btn')?.click();
        return;
      }
      try {
        saveBtn.disabled = true;
        await saveSequenceToCloud(activeIdx);
        showToast("Saved: " + (activeSeq.name || "timeline"));
        renderSequenceManager();
      } catch (e) {
        showToast("Save failed: " + e.message);
        saveBtn.disabled = false;
      }
    });
    actionsDiv.appendChild(saveBtn);

    if (activeSeq?._cloudId) {
      if (activeSeq._isPublic) {
        const unpubBtn = document.createElement("button");
        unpubBtn.className = "btn-sm";
        unpubBtn.textContent = "Unpublish";
        unpubBtn.addEventListener("click", async () => {
          try {
            await unpublishSequence(activeIdx);
            showToast("Unpublished");
            renderSequenceManager();
          } catch (e) {
            showToast("Unpublish failed: " + e.message);
          }
        });
        actionsDiv.appendChild(unpubBtn);
      } else {
        const pubBtn = document.createElement("button");
        pubBtn.className = "btn-sm btn-publish";
        pubBtn.textContent = "Publish";
        pubBtn.addEventListener("click", async () => {
          try {
            await publishSequence(activeIdx);
            showToast("Published! Visible on Explore");
            renderSequenceManager();
          } catch (e) {
            showToast("Publish failed: " + e.message);
          }
        });
        actionsDiv.appendChild(pubBtn);
      }

      const cloudDelBtn = document.createElement("button");
      cloudDelBtn.className = "btn-sm btn-danger-outline";
      cloudDelBtn.textContent = "Remove from cloud";
      cloudDelBtn.addEventListener("click", () => {
        showConfirmModal("Remove this timeline from the cloud? (Local copy stays.)", async () => {
          try {
            await deleteSequenceFromCloud(activeIdx);
            showToast("Removed from cloud");
            renderSequenceManager();
          } catch (e) {
            showToast("Remove failed: " + e.message);
          }
        });
      });
      actionsDiv.appendChild(cloudDelBtn);
    }
  }

  if (state.project.sequences.length > 1) {
    const sep2 = document.createElement("span");
    sep2.style.cssText = "width:1px;background:var(--border);height:16px;margin:0 2px;";
    actionsDiv.appendChild(sep2);

    const delBtn = document.createElement("button");
    delBtn.className = "btn-sm btn-danger";
    delBtn.textContent = "Delete locally";
    delBtn.addEventListener("click", deleteActiveSequence);
    actionsDiv.appendChild(delBtn);
  }

  container.appendChild(actionsDiv);
}

function addNewSequence() {
  pushUndo();
  const idx = state.project.sequences.length + 1;
  state.project.sequences.push({ name: "Timeline " + idx, steps: [], loop: true });
  state.ui.activeSequenceIdx = state.project.sequences.length - 1;
  state.ui.playbackStep = 0;
  notify("addSequence");
  renderSequenceManager();
  renderTimeline();
  renderTimelinePreview();
  renderTimelineControls();
}

function deleteActiveSequence() {
  if (state.project.sequences.length <= 1) return;
  const seq = getActiveSequence();
  const seqName = seq ? seq.name : "this timeline";
  const msg = seq?._cloudId
    ? "Delete \"" + seqName + "\" locally? (It will stay on the cloud — use 'Remove from cloud' first if you want it fully gone.)"
    : "Delete \"" + seqName + "\"?";
  showConfirmModal(msg, () => {
    pushUndo();
    state.project.sequences.splice(state.ui.activeSequenceIdx, 1);
    state.ui.activeSequenceIdx = Math.max(0, state.ui.activeSequenceIdx - 1);
    state.ui.playbackStep = 0;
    stopPlayback();
    notify("deleteSequence");
    renderSequenceManager();
    renderTimeline();
    renderTimelinePreview();
    renderTimelineControls();
  });
}

function renameActiveSequence() {
  const seq = getActiveSequence();
  if (!seq) return;
  showPromptModal("Rename Timeline", seq.name, (newName) => {
    if (!newName || !newName.trim()) return;
    pushUndo();
    seq.name = newName.trim();
    notify("renameSequence");
    renderSequenceManager();
  });
}

function duplicateActiveSequence() {
  const seq = getActiveSequence();
  if (!seq) return;
  pushUndo();
  const dup = JSON.parse(JSON.stringify(seq));
  dup.name = seq.name + " copy";
  state.project.sequences.push(dup);
  state.ui.activeSequenceIdx = state.project.sequences.length - 1;
  state.ui.playbackStep = 0;
  notify("dupSequence");
  renderSequenceManager();
  renderTimeline();
  renderTimelinePreview();
  renderTimelineControls();
}

// ================================================================
// TIMELINE STRIP
// ================================================================
export function renderTimeline() {
  const strip = document.getElementById("timeline-strip");
  strip.innerHTML = "";
  const seq = getActiveSequence();
  if (!seq) return;

  seq.steps.forEach((step, idx) => {
    const kf = state.project.keyframes[step.keyframeId];
    if (!kf) return;

    const el = document.createElement("div");
    el.className = "timeline-step";
    if (state.ui.isPlaying && state.ui.playbackStep === idx) el.classList.add("playing");
    el.draggable = true;
    el.dataset.idx = idx;

    // Mini preview
    const preview = document.createElement("div");
    preview.className = "timeline-step-preview";
    preview.textContent = keyframeToText(kf);
    if (kf.backgroundColor || state.project.backgroundColor) {
      preview.style.backgroundColor = kf.backgroundColor || state.project.backgroundColor;
    }
    el.appendChild(preview);

    // Name
    const name = document.createElement("div");
    name.className = "timeline-step-name";
    name.textContent = kf.name;
    el.appendChild(name);

    // Duration
    const durDiv = document.createElement("div");
    durDiv.className = "timeline-step-duration";
    const durInput = document.createElement("input");
    durInput.type = "number";
    durInput.value = step.duration;
    durInput.min = 50;
    durInput.step = 50;
    durInput.addEventListener("change", () => {
      pushUndo();
      step.duration = Math.max(50, parseInt(durInput.value) || 500);
      notify("duration");
    });
    const msLabel = document.createElement("span");
    msLabel.textContent = "ms";
    durDiv.appendChild(durInput);
    durDiv.appendChild(msLabel);
    el.appendChild(durDiv);

    // Delete
    const del = document.createElement("button");
    del.className = "step-delete";
    del.textContent = "\u00D7";
    del.addEventListener("click", (e) => {
      e.stopPropagation();
      pushUndo();
      seq.steps.splice(idx, 1);
      notify("removeStep");
      renderTimeline();
    });
    el.appendChild(del);

    // Drag events
    el.addEventListener("dragstart", (e) => {
      dragState = idx;
      el.classList.add("dragging");
      e.dataTransfer.effectAllowed = "move";
    });
    el.addEventListener("dragend", () => {
      el.classList.remove("dragging");
      dragState = null;
      document.querySelectorAll(".timeline-step").forEach(s => s.classList.remove("drag-over"));
    });
    el.addEventListener("dragover", (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      el.classList.add("drag-over");
    });
    el.addEventListener("dragleave", () => {
      el.classList.remove("drag-over");
    });
    el.addEventListener("drop", (e) => {
      e.preventDefault();
      el.classList.remove("drag-over");
      if (dragState === null) return;
      const from = dragState;
      const to = idx;
      if (from === to) return;
      pushUndo();
      const [moved] = seq.steps.splice(from, 1);
      seq.steps.splice(to, 0, moved);
      notify("reorderSteps");
      renderTimeline();
    });

    strip.appendChild(el);
  });

  // Add step button
  const addBtn = document.createElement("button");
  addBtn.className = "timeline-add-step";
  addBtn.textContent = "+ Add";
  addBtn.addEventListener("click", (e) => {
    showAddStepDropdown(e.currentTarget);
  });
  strip.appendChild(addBtn);
}

function showAddStepDropdown(anchorEl) {
  const dropdown = document.getElementById("timeline-add-dropdown");
  dropdown.innerHTML = "";
  dropdown.classList.toggle("open");

  const seq = getActiveSequence();

  for (const kf of Object.values(state.project.keyframes)) {
    const btn = document.createElement("button");
    btn.textContent = kf.name;
    btn.addEventListener("click", () => {
      pushUndo();
      seq.steps.push({ keyframeId: kf.id, duration: 500 });
      dropdown.classList.remove("open");
      notify("addStep");
      renderTimeline();
    });
    dropdown.appendChild(btn);
  }

  // Position
  const rect = anchorEl.getBoundingClientRect();
  const parent = dropdown.parentElement.getBoundingClientRect();
  dropdown.style.left = (rect.left - parent.left) + "px";
  dropdown.style.top = (rect.bottom - parent.top + 4) + "px";

  const close = (e) => {
    if (!dropdown.contains(e.target) && e.target !== anchorEl) {
      dropdown.classList.remove("open");
      document.removeEventListener("click", close);
    }
  };
  setTimeout(() => document.addEventListener("click", close), 0);
}

// ================================================================
// TIMELINE PLAYBACK
// ================================================================
let playbackTimer = null;

export function renderTimelineControls() {
  const container = document.getElementById("timeline-controls");
  container.innerHTML = "";

  const stepBackBtn = document.createElement("button");
  stepBackBtn.textContent = "|<";
  stepBackBtn.addEventListener("click", () => stepPlayback(-1));
  container.appendChild(stepBackBtn);

  const playBtn = document.createElement("button");
  playBtn.textContent = state.ui.isPlaying ? "Pause" : "Play";
  if (state.ui.isPlaying) playBtn.classList.add("active");
  playBtn.addEventListener("click", togglePlayback);
  container.appendChild(playBtn);

  const stepFwdBtn = document.createElement("button");
  stepFwdBtn.textContent = ">|";
  stepFwdBtn.addEventListener("click", () => stepPlayback(1));
  container.appendChild(stepFwdBtn);

  const speedLabel = document.createElement("label");
  speedLabel.textContent = "Speed: ";
  const speedSelect = document.createElement("select");
  [0.25, 0.5, 1, 1.5, 2, 4].forEach(s => {
    const opt = document.createElement("option");
    opt.value = s;
    opt.textContent = s + "x";
    if (state.ui.playbackSpeed === s) opt.selected = true;
    speedSelect.appendChild(opt);
  });
  speedSelect.addEventListener("change", () => {
    state.ui.playbackSpeed = parseFloat(speedSelect.value);
  });
  speedLabel.appendChild(speedSelect);
  container.appendChild(speedLabel);

  const seq = getActiveSequence();
  const loopLabel = document.createElement("label");
  const loopCheck = document.createElement("input");
  loopCheck.type = "checkbox";
  loopCheck.checked = state.ui.loop;
  loopCheck.addEventListener("change", () => {
    state.ui.loop = loopCheck.checked;
    if (seq) seq.loop = loopCheck.checked;
  });
  loopLabel.appendChild(loopCheck);
  loopLabel.appendChild(document.createTextNode(" Loop"));
  container.appendChild(loopLabel);
}

export function togglePlayback() {
  if (state.ui.isPlaying) {
    stopPlayback();
  } else {
    startPlayback();
  }
  renderTimelineControls();
}

function startPlayback() {
  const seq = getActiveSequence();
  if (!seq || seq.steps.length === 0) return;
  state.ui.isPlaying = true;
  state.ui.playbackStep = 0;
  playNextStep();
}

export function stopPlayback() {
  state.ui.isPlaying = false;
  clearTimeout(playbackTimer);
  renderTimeline();
  renderTimelineControls();
}

function playNextStep() {
  const seq = getActiveSequence();
  if (!seq || !state.ui.isPlaying) return;

  const steps = seq.steps;
  if (steps.length === 0) { stopPlayback(); return; }

  if (state.ui.playbackStep >= steps.length) {
    if (state.ui.loop) {
      state.ui.playbackStep = 0;
    } else {
      stopPlayback();
      return;
    }
  }

  const step = steps[state.ui.playbackStep];
  const kf = state.project.keyframes[step.keyframeId];
  if (kf) {
    renderTimelinePreviewFrame(kf);
  }
  renderTimeline();

  const delay = step.duration / state.ui.playbackSpeed;
  playbackTimer = setTimeout(() => {
    state.ui.playbackStep++;
    playNextStep();
  }, delay);
}

function stepPlayback(dir) {
  const seq = getActiveSequence();
  if (!seq || seq.steps.length === 0) return;
  stopPlayback();
  state.ui.playbackStep = Math.max(0, Math.min(seq.steps.length - 1, state.ui.playbackStep + dir));
  const step = seq.steps[state.ui.playbackStep];
  const kf = state.project.keyframes[step.keyframeId];
  if (kf) renderTimelinePreviewFrame(kf);
  renderTimeline();
  renderTimelineControls();
}

export function renderTimelinePreview() {
  const seq = getActiveSequence();
  if (!seq || seq.steps.length === 0) {
    document.getElementById("timeline-preview").textContent = "(add keyframes to timeline)";
    return;
  }
  const step = seq.steps[0];
  const kf = state.project.keyframes[step.keyframeId];
  if (kf) renderTimelinePreviewFrame(kf);
}

export function renderTimelinePreviewFrame(kf) {
  const el = document.getElementById("timeline-preview");
  el.style.fontSize = state.project.fontSize + "px";
  el.style.backgroundColor = kf.backgroundColor || state.project.backgroundColor || "";
  el.innerHTML = "";

  for (let r = 0; r < kf.rows; r++) {
    for (let c = 0; c < kf.cols; c++) {
      const cell = kf.grid[r][c];
      const span = document.createElement("span");
      span.style.color = cell.color || kf.defaultColor;
      span.textContent = cell.char || " ";
      el.appendChild(span);
    }
    if (r < kf.rows - 1) el.appendChild(document.createTextNode("\n"));
  }
}

// ================================================================
// TIMELINE EXPORT BUTTONS
// ================================================================
export function renderTimelineExport() {
  const container = document.getElementById("timeline-export");
  container.innerHTML = "";

  const btns = [
    { label: "Export HTML", fn: exportHTML },
    { label: "Export All HTML", fn: exportAllHTML },
    { label: "Export JSON", fn: exportFullJSON },
    { label: "Copy JS", fn: copyJS },
  ];

  for (const b of btns) {
    const btn = document.createElement("button");
    btn.textContent = b.label;
    btn.addEventListener("click", b.fn);
    container.appendChild(btn);
  }
}
